import { useTranslation } from "react-i18next";
import Text from "@/components/atoms/Text";
import { Contract, ContractAttachment } from "@/types";
import { FC, useEffect, useState } from "react";
import Accordion from "@/components/atoms/accordions/DrawerAccordion";
import Group from "@/components/atoms/Group";
import DocumentList from "@/components/molecules/DocumentList";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import { useGetContractAttachments } from "@/services/Contracts";

interface ContractAttachmentsTabProps {
  contract: Contract;
}

const ContractAttachmentsTab: FC<ContractAttachmentsTabProps> = ({ contract }) => {
  const { t } = useTranslation();

  const [images, setImages] = useState<ContractAttachment[]>([]);
  const [attachments, setAttachments] = useState<ContractAttachment[]>([]);

  const { data: attachmentsAndImages, isLoading: isLoadingAttachments } = useGetContractAttachments(contract.id);

  useEffect(() => {
    if (attachmentsAndImages && attachmentsAndImages.length > 0) {
      const imgs = attachmentsAndImages.filter((doc) => doc.mediaType && !doc.mediaType.startsWith("application/"));
      const atts = attachmentsAndImages.filter((doc) => doc.mediaType && doc.mediaType.startsWith("application/"));
      setImages(imgs);
      setAttachments(atts);
    }
  }, [attachmentsAndImages]);

  const AccordionItems = [
    {
      value: "attachments" as const,
      label: t("common:attachments"),
      controller: (
        <Accordion.Control>
          <Group>
            <Text size="sm" display="technical" tt="uppercase" strong={true}>
              {t("common:attachments")}
            </Text>
          </Group>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          {attachments.length > 0 ? (
            <DocumentList files={attachments || []} origin={"api"} />
          ) : (
            <Text>{t("common:noAttachmentsFound")}</Text>
          )}
        </Accordion.Panel>
      ),
    },
    {
      value: "images" as const,
      label: t("common:images"),
      controller: (
        <Accordion.Control>
          <Group>
            <Text size="sm" display="technical" tt="uppercase" strong={true}>
              {t("common:images")}
            </Text>
          </Group>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          {images.length > 0 ? (
            <ImageCarousel images={images || []} isLoading={isLoadingAttachments} />
          ) : (
            <Text>{t("common:noImagesFound")}</Text>
          )}
        </Accordion.Panel>
      ),
    },
  ];

  return (
    <Accordion unstyled defaultValue={AccordionItems[0]?.value}>
      {AccordionItems.map((item, index) => (
        <Accordion.Item value={item?.value} key={index}>
          {item.controller}
          {item.panel}
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

export default ContractAttachmentsTab;
